import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import { bootstrapNative, isNativeApp } from "@/native/platform";
import { installErrorReporter, reportError } from "@/utils/errorReporter";
import { installOverlayDragGuard } from "@/utils/overlayDragGuard";

// Capture uncaught errors as early as possible (before app bootstrap).
installErrorReporter();

// A drag that starts inside a modal and ends on its backdrop must not close it.
installOverlayDragGuard();

/**
 * Native: the launch screen disappears the moment the app window appears, but
 * the webview is still blank for a second or so while React boots — which reads
 * as a black flash. SplashScreen is configured with launchAutoHide:false so it
 * stays over the webview until we dismiss it here, once content exists.
 *
 * This MUST run on every exit path from bootstrap, including the failure one.
 * launchShowDuration is not a backstop: the plugin only schedules the auto-hide
 * timer when autoHide is true (SplashScreen.swift — `if settings.autoHide`), so
 * with launchAutoHide:false nothing else will ever take the splash down. If this
 * call is skipped the user is left staring at the splash forever, with no error
 * on screen and no way forward but force-quitting.
 */
function hideNativeSplash() {
  if (!isNativeApp()) return;
  requestAnimationFrame(async () => {
    try {
      const { SplashScreen } = await import("@capacitor/splash-screen");
      await SplashScreen.hide();
    } catch { /* plugin absent — nothing to hide */ }
  });
}

/**
 * Last-resort boot failure screen, painted straight into #root.
 *
 * Deliberately dependency-free — no React, no Tailwind classes, no imports.
 * The reason we are here at all may be that a lazy chunk failed to load (a
 * half-finished `cap sync` leaves exactly that state in the native app), so
 * anything that needs another module could fail the same way and leave the
 * screen blank again. Inline styles for the same reason: index.css is imported
 * statically above, but a boot this broken shouldn't assume it applied.
 */
function renderBootFailure() {
  const root = document.getElementById("root");
  if (!root) return;
  root.innerHTML = `
    <div style="position:fixed;inset:0;display:flex;flex-direction:column;
                align-items:center;justify-content:center;padding:0 2rem;
                text-align:center;background:#0F172A;color:#fff;
                font-family:Manrope,system-ui,sans-serif">
      <h1 style="font-size:1.25rem;font-weight:700;margin:0 0 .5rem">
        WellTrack couldn&rsquo;t start
      </h1>
      <p style="font-size:.875rem;color:#94a3b8;max-width:20rem;margin:0 0 2rem">
        Something went wrong while loading. Check your connection and try again.
      </p>
      <button type="button" id="wt-boot-retry"
              style="height:3rem;width:100%;max-width:20rem;border:0;border-radius:.75rem;
                     background:#fff;color:#0f172a;font-size:.875rem;font-weight:600;
                     cursor:pointer">
        Try again
      </button>
    </div>`;
  root.querySelector("#wt-boot-retry")
    ?.addEventListener("click", () => window.location.reload());
}

// App is imported dynamically AFTER native bootstrap so that api.js (imported
// by App) sees window.__WT_SCHOOL_SLUG__ when computing its baseURL. On the
// web this resolves immediately and behaves exactly as before.
(async () => {
  try {
    await bootstrapNative();

    const [{ default: App }, { Toaster }, { default: AppErrorBoundary }] = await Promise.all([
      import("@/App"),
      import("@/components/ui/toaster"),
      import("@/components/AppErrorBoundary"),
    ]);

    const root = ReactDOM.createRoot(document.getElementById("root"));
    root.render(
      <React.StrictMode>
        <AppErrorBoundary>
          <App />
          <Toaster />
        </AppErrorBoundary>
      </React.StrictMode>,
    );
  } catch (err) {
    // AppErrorBoundary cannot help here — the failure happened before (or while)
    // it was being imported, so nothing is mounted to catch anything.
    console.error("WellTrack failed to start", err);
    try {
      reportError(`Boot failure: ${err?.message || err}`, err?.stack, "bootstrap");
    } catch { /* reporting is best-effort; never mask the original failure */ }
    renderBootFailure();
  } finally {
    hideNativeSplash();
  }
})();

// Register service worker for PWA/offline support and Web Push notifications.
// Must run after render so the initial page load isn't delayed.
// Skipped in the native app — it uses native networking and (later) APNs push.
if ('serviceWorker' in navigator && !isNativeApp()) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(err => {
      console.warn('Service worker registration failed:', err);
    });
  });
}
